// /components/reports/Options.js
import React, { useEffect, useState, useMemo } from 'react';
import { Form, Card, Stack } from 'react-bootstrap';
import { getClassesWithFilterQuery } from '../../utils/class';
import { ErrorMsg } from '../../utils/Toast';
import { REPORT_KINDS } from './reportsEnums';

// Import the new smaller components
import ClassSelector from './components/classSelector';
import KindSelector from './components/kindSelector';
import PeriodicOptions from './components/periodicOptions';
import LessonOptions from './components/lessonOptions';
import MockOptions from './components/MockOptions';
import StudentSelector from './components/studentSelector';
import ActionButtons from './components/ActionButtons'
// import SendReportToClass from './components/sendReports/sendReportToClass';

function Options({ options, setOptions, onSendEmail, onSendWhatsApp }) {
  const [classes, setClasses] = useState([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);

  // --- Fetch Classes (Shared Data) ---
  // We fetch this here because StudentSelector needs the populated 'studentsIDS'
  useEffect(() => {
    const fetchClasses = async () => {
      setIsLoadingClasses(true);
      try {
        const res = await getClassesWithFilterQuery({ active: true, populate: 'studentsIDS' });
        setClasses(res.data.classes || []);
      } catch (e) {
        ErrorMsg('Failed to fetch classes');
      } finally {
        setIsLoadingClasses(false);
      }
    };
    fetchClasses();
  }, []);

  // --- Memoized Student List ---
  // This is derived from the fetched classes and passed to StudentSelector
  const studentsInClass = useMemo(() => {
    if (!options.selectedClass) return [];
    const selectedClassObj = classes.find((cls) => cls._id === options.selectedClass);
    return selectedClassObj?.studentsIDS || [];
  }, [options.selectedClass, classes]);

  // --- Event Handlers ---

  // Generic handler for simple dropdowns and date pickers
  const handleChange = (e) => {
    const { name, value } = e.target;

    setOptions((prev) => {
      const newState = { ...prev, [name]: value };

      // Reset cascading state
      if (name === 'selectedClass') {
        Object.assign(newState, {
          reportKind: '',
          periodicType: '',
          from: '',
          to: '',
          selectedLessons: [],
          selectedMock: '',
          selectedUsers: [],
        });
      }
      if (name === 'reportKind') {
        Object.assign(newState, {
          periodicType: '',
          from: '',
          to: '',
          selectedLessons: [],
          selectedMock: '',
        });
      }
      return newState;
    });
  };

  // Special handler for multi-select (Lessons and Students)
  const handleMultiSelectChange = (e) => {
    const { name } = e.target;
    const selectedIds = Array.from(e.target.selectedOptions, (option) => option.value);
    setOptions((prev) => ({
      ...prev,
      [name]: selectedIds,
    }));
  };

  // --- Conditional Flags for UI ---
  const showKindDropdown = options.selectedClass;
  const showPeriodicOptions = options.reportKind === REPORT_KINDS.PERIODIC;
  const showLessonOptions = options.reportKind === REPORT_KINDS.LESSON;
  const showMockOptions = options.reportKind === REPORT_KINDS.MOCK;
  const showStudentOptions = options.selectedClass && options.reportKind;

  return (
    <Card>
      <Card.Header as="h5">Report Options</Card.Header>
      <Card.Body>
        <Form>
          <Stack gap={3}>
            {/* 1. Select Class */}
            <ClassSelector
              classes={classes}
              selectedClass={options.selectedClass}
              onChange={handleChange}
              isLoading={isLoadingClasses}
            />

            {/* 2. Choose Report Kind */}
            {showKindDropdown && (
              <KindSelector
                reportKind={options.reportKind}
                onChange={handleChange}
              />
            )}

            {/* 3. Conditional Options */}
            {showPeriodicOptions && (
              <PeriodicOptions
                periodicType={options.periodicType}
                from={options.from}
                to={options.to}
                onChange={handleChange}
              />
            )}

            {showLessonOptions && (
              <LessonOptions
                selectedClass={options.selectedClass}
                selectedLessons={options.selectedLessons}
                onChange={handleMultiSelectChange}
              />
            )}

            {showMockOptions && (
              <MockOptions
                selectedClass={options.selectedClass}
                selectedMock={options.selectedMock}
                onChange={handleChange}
              />
            )}

            {/* 4. Select Students (Optional) */}
            {showStudentOptions && (
              <StudentSelector
                students={studentsInClass}
                selectedUsers={options.selectedUsers}
                onChange={handleMultiSelectChange}
              />
            )}

            {/* 5. Action Buttons */}
            {/* send whatsapp report to students / parents */}
            {/* send email report to students / parents */}
            {/* report types (mock activity , (periodic , lesson)) */}
            {/* <ActionButtons
              onSendEmail={onSendEmail}
              onSendWhatsApp={onSendWhatsApp}
              disabled={!showStudentOptions}
            /> */}
            {/* <SendReportToClass classId={options.selectedClass} type={'Activity'} /> */}
          </Stack>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default Options;